import { Children, ReactElement, useState, useRef } from 'react';
import Option, { OptionProps } from './Option';
import useFetchOptions from '../hooks/useFetchOptions';
import { like } from '../../../utils/easy';
import { InputAttributes, InputChangeEvent, NameValue } from '../types';

export interface SelectSearchProps extends InputAttributes {
  disabled?: boolean;
  options?: NameValue[];
  itemHeight?: number;
  value?: string;
  maxListHeight?: string | number;
  onChange?: (value: any) => void;
  getOptions?: () => Promise<NameValue[]>;
}

const SelectSearch = ({
  options,
  disabled,
  getOptions,
  itemHeight,
  value,
  maxListHeight,
  children: propsChildren,
  onChange,
  ...other
}: SelectSearchProps) => {

  const valueRef = useRef(undefined);

  const [, setValue] = useState(value);
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState(value);
  valueRef.current = value;

  if (!itemHeight) {
    itemHeight = 40;
  }

  let [data] = useFetchOptions(getOptions, options ?? []);
  let children: ReactElement<OptionProps>[];

  if (search !== '') {
    data = data.filter(d => like(d.name, search) || like(d.value, search));
  }

  if (propsChildren == null) {
    propsChildren = data.map((d, i) => <Option key={i} value={d.value}>{d.name}</Option>);
  }

  children = Children.toArray(propsChildren) as any[];

  const handleSearchChange = (event: InputChangeEvent) => {
    setOpen(true);
    setSearch(event.target.value);
  };

  const handleValueChange = (newValue: string, event: InputChangeEvent) => {
    setOpen(false);
    valueRef.current = newValue;
    setValue(newValue);
    setSearch(newValue);
    if (onChange) {
      onChange(newValue);
    }
  }

  const handleKeydown = (e) => {
    if (e.key === 'Enter' && data.length > 0) {
      valueRef.current = data[0].value;
      setValue(data[0].value);
      setSearch(data[0].name);
      setOpen(false);
    }
  }

  const handleFocus = () => setOpen(true);

  const handleBlur = () => setOpen(false);

  const getOpenClass = () => isOpen ? ' is-open' : '';

  const getDisabledClass = () => disabled ? ' disabled' : '';

  const getHeight = () => isOpen ? (itemHeight * data.length) + 2 : 0;

  const getValue = () => {
    if (valueRef.current && valueRef.current === search) {
      return valueRef.current;
    }
    if (search == null) {
      return value;
    }
    return search;
  }

  return (
    <div className={'widget-select-search-cmp' + getOpenClass() + getDisabledClass()}>
      <div className="input-wrapper">
        <input
          type="text"
          {...other}
          value={getValue() || ''}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeydown}
        />
        <i className="search-icon far fa-search"></i>
      </div>
      <div
        className="widget-select-options"
        style={{ height: getHeight(), ...{ maxHeight: maxListHeight ?? undefined, overflowY: maxListHeight ? 'auto' : 'inherit' } }}>
        {children.map(child => (
          <div key={child.key}
            className="widget-option-item-wrapper"
            onClick={handleValueChange.bind(this, child.props.value)}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};

SelectSearch.Option = Option;

export default SelectSearch;
